import { noopPromise, RequestContext, BaseApiRequest, XcoreCore } from "@robe/core";
import { useMemo } from "react";
import useSWR, { SWRConfiguration, SWRResponse } from "swr";
import { useRequestContext } from "./useRequestContext";

export interface XcoreQueryConfiguration {
  ctx: Partial<RequestContext>;
}

export const useXcoreQuery = <T>(
  req: (core: XcoreCore) => BaseApiRequest<T> | null,
  config?: SWRConfiguration & XcoreQueryConfiguration
): SWRResponse<T | undefined> => {
  const { swr: _, ...ctxContext } = useRequestContext();
  const ctx = useMemo(() => ({ ...ctxContext, ...config?.ctx }), [config?.ctx, ctxContext]);

  const query = useMemo(() => {
    if (ctx === null) {
      // eslint-disable-next-line max-len
      throw new Error(
        "Request context is not specified. Use either RequestContextProvider or XcoreClientProvider to provide context. Or pass ctx option in the configuration object."
      );
    }

    const core = new XcoreCore({
      ...ctx,
      requestHandler: (reqUrl) => noopPromise(reqUrl, Promise.resolve({ noop: true }) as any),
    });

    return req(core);
  }, [ctx, req]);

  return useSWR(
    query?.url ?? null,
    async () => {
      const core = new XcoreCore(ctx);
      return await req(core)?.catch((e) => {
        console.trace({ e });
        ctx.onError?.(e);
        throw e;
      });
    },
    config
  );
};
